import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Package, ShoppingCart, FolderOpen, File } from "lucide-react";

/** Slugify a tipo_documento string for use in URLs */
export function toSlug(tipo: string): string {
  return tipo
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ICON_MAP: Record<string, any> = {
  factura: FileText,
  "packing-list": Package,
  "ordenes-de-compra": ShoppingCart,
};

export function getIconForSlug(slug: string) {
  return ICON_MAP[slug] || File;
}

export interface NavTab {
  title: string;
  slug: string;
  icon: any;
  tipoDocumento: string;
}

const DEFAULT_TABS: NavTab[] = [
  { title: "Facturas", slug: "facturas", icon: FileText, tipoDocumento: "Factura Internacional" },
  { title: "Packing Lists", slug: "packing-lists", icon: Package, tipoDocumento: "Packing List" },
  { title: "Órdenes de Compra", slug: "ordenes-de-compra", icon: ShoppingCart, tipoDocumento: "Orden de Compra" },
];

export function useDocumentTypes() {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["document-types", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("registro_documentos")
        .select("tipo_documento")
        .eq("org_id", orgId!);
      if (error) throw error;

      const unique = Array.from(
        new Set(
          (data ?? [])
            .map((d) => d.tipo_documento)
            .filter((t): t is string => !!t)
        )
      );

      if (unique.length === 0) return DEFAULT_TABS;

      const tabs: NavTab[] = unique.map((tipo) => {
        const slug = toSlug(tipo);
        return { title: tipo, slug, icon: getIconForSlug(slug), tipoDocumento: tipo };
      });

      const knownOrder = ["factura", "packing-list", "ordenes-de-compra"];
      tabs.sort((a, b) => {
        const ai = knownOrder.indexOf(a.slug);
        const bi = knownOrder.indexOf(b.slug);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.title.localeCompare(b.title);
      });

      return tabs;
    },
    enabled: !!user && !!orgId,
    staleTime: 30_000,
  });
}
